"use client";

import {
  Button,
  FormNumber,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  type ShopBranchOption,
  type ShopSellableCatalogRow,
  shopCatalogApi,
  shopInventoryApi,
} from "@/modules/merchant/presentation/api/client";
import {
  usePatchInventoryLotScanCodeMutation,
  useReceiveShopGoodsMutation,
  useShopInventoryBranchSkuStock,
  useShopInventoryLotLabelsQuery,
} from "@/modules/merchant/presentation/api/queries";
import {
  buildDefaultUnitScanCode,
  printLotLabelCode128,
  ReceiveFormSchema,
  type ReceiveFormValues,
  SerialLotGroupBlock,
  trackingLabel,
} from "@/modules/merchant/presentation/inventory/receive-goods-shared";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { DatePickerField } from "@/modules/merchant/presentation/ui/DatePickerField";
import {
  InventorySkuStockBanner,
  InventoryStockHero,
  InventoryStockLoading,
} from "@/modules/merchant/presentation/ui/InventorySkuStockBanner";
import { ReceiveWizardProgress } from "@/modules/merchant/presentation/ui/ReceiveWizardProgress";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopWarehouseLocationPickerRemote } from "@/modules/merchant/presentation/ui/ShopWarehouseLocationPicker";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const RECEIVE_ROUTE = "/app/shops/$shopId/inventory/receive" as const;

type ReceiveSearch = {
  branchId?: string;
  shopProductSkuId?: string;
};

export function ShopInventoryReceivePage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const search = (useSearch({ strict: false }) ?? {}) as ReceiveSearch;

  const urlSkuId = search.shopProductSkuId;

  const skuQuery = useQuery({
    queryKey: ["shops", shopId, "receive-goods", "sku", urlSkuId],
    queryFn: () => {
      if (!shopId || !urlSkuId) throw new Error("missing");
      return shopCatalogApi.getSellableSku(shopId, urlSkuId);
    },
    enabled: !!shopId && !!urlSkuId,
  });

  const activeSku: ShopSellableCatalogRow | null = skuQuery.data ?? null;

  const receive = useReceiveShopGoodsMutation(shopId);
  const patchLotScan = usePatchInventoryLotScanCodeMutation(shopId);
  const { currentShop } = useMerchantShops(shopId);
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );
  const [receiveLocationId, setReceiveLocationId] = useState("");
  const [lotScanDraftById, setLotScanDraftById] = useState<
    Record<string, string>
  >({});
  const [wizardStep, setWizardStep] = useState(1);

  const form = RHF.useForm<ReceiveFormValues>({
    resolver: zodResolver(ReceiveFormSchema) as Resolver<ReceiveFormValues>,
    defaultValues: {
      branchId: search.branchId ?? "",
      qty: 1,
      lots: [{ lotNo: "", qty: 1, manufacturedDate: "", expiryDate: "" }],
      serialLotGroups: [
        { lotNo: "", items: [{ serialNo: "", unitScanCode: "" }] },
      ],
      note: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lots",
  });

  const {
    fields: serialGroupFields,
    append: appendSerialGroup,
    remove: removeSerialGroup,
  } = useFieldArray({
    control: form.control,
    name: "serialLotGroups",
  });

  useEffect(() => {
    form.reset({
      branchId: search.branchId ?? "",
      qty: 1,
      lots: [{ lotNo: "", qty: 1, manufacturedDate: "", expiryDate: "" }],
      serialLotGroups: [
        { lotNo: "", items: [{ serialNo: "", unitScanCode: "" }] },
      ],
      note: "",
    });
    setWizardStep(1);
  }, [search.branchId, urlSkuId, form]);

  const rawTracking = activeSku?.trackingMode;
  const mode =
    rawTracking === "lot" || rawTracking === "lot_serial"
      ? rawTracking
      : rawTracking === "serial"
        ? "lot_serial"
        : "none";

  const watchedBranch = form.watch("branchId");
  const branchIdForStock = (() => {
    const w = typeof watchedBranch === "string" ? watchedBranch.trim() : "";
    if (w) return w;
    return search.branchId?.trim() || undefined;
  })();

  const branchStock = useShopInventoryBranchSkuStock(shopId, {
    branchId: branchIdForStock,
    shopProductSkuId: urlSkuId,
  });
  const stockLine = branchStock.data?.data[0];
  const lotLabelsQuery = useShopInventoryLotLabelsQuery(shopId, {
    branchId: branchIdForStock,
    shopProductSkuId: urlSkuId,
    enabled: mode === "lot" && wizardStep === 2,
  });

  const branchDetailQuery = useQuery({
    queryKey: ["shops", shopId, "receive-wizard", "branch", branchIdForStock],
    queryFn: () => {
      if (!shopId || !branchIdForStock) throw new Error("missing");
      return shopInventoryApi.getBranch(shopId, branchIdForStock);
    },
    enabled: !!shopId && !!branchIdForStock && wizardStep === 3,
  });

  const canAdvanceFromStep1 =
    !!form.watch("branchId")?.trim() && !!urlSkuId && !!activeSku;

  const validateStep2 = (): boolean => {
    const vals = form.getValues();
    if (mode === "none") {
      if (!vals.qty || vals.qty < 1) {
        toast.error("ໃສ່ຈຳນວນຮັບເຂົ້າ");
        return false;
      }
      return true;
    }
    if (mode === "lot") {
      const lots = vals.lots.filter((l) => l.lotNo.trim().length > 0);
      if (lots.length === 0) {
        toast.error("ໃສ່ຢ່າງນ້ອຍ 1 lot ທີ່ມີເລກ lot");
        return false;
      }
      return true;
    }
    if (mode === "lot_serial") {
      let serialCount = 0;
      for (const g of vals.serialLotGroups) {
        const lotNo = g.lotNo.trim();
        for (const it of g.items) {
          if (it.serialNo.trim()) {
            if (!lotNo) {
              toast.error("ໃສ່ Lot no. ກ່ອນ ສຳລັບທຸກກຸ່ມທີ່ມີ serial");
              return false;
            }
            serialCount += 1;
          }
        }
      }
      if (serialCount === 0) {
        toast.error("ໃສ່ຢ່າງນ້ອຍ 1 serial");
        return false;
      }
      return true;
    }
    return true;
  };

  const receiveSummaryLine = (): string => {
    const vals = form.getValues();
    if (mode === "none") {
      return `${vals.qty} ຊິ້ນ`;
    }
    if (mode === "lot") {
      const lots = vals.lots.filter((l) => l.lotNo.trim().length > 0);
      const totalQty = lots.reduce((sum, l) => sum + (l.qty || 0), 0);
      return `${lots.length} lot · ${totalQty} ຊິ້ນ`;
    }
    let serialCount = 0;
    for (const g of vals.serialLotGroups) {
      for (const it of g.items) {
        if (it.serialNo.trim()) serialCount += 1;
      }
    }
    return `${serialCount} serial`;
  };

  const goReceiveSearch = (next: ReceiveSearch) => {
    if (!shopId) return;
    nav({
      to: RECEIVE_ROUTE,
      params: { shopId },
      search: {
        branchId: next.branchId,
        shopProductSkuId: next.shopProductSkuId,
      },
      replace: true,
    });
  };

  const onSubmit = async (vals: ReceiveFormValues) => {
    if (!shopId || !activeSku) return;
    const branchId = vals.branchId?.trim();
    if (!branchId) {
      toast.error("ເລືອກສາຂາ");
      return;
    }

    const skuId = activeSku.id;
    const note = vals.note?.trim() || undefined;

    try {
      if (mode === "none") {
        await receive.mutateAsync({
          branchId,
          defaultLocationId: receiveLocationId || undefined,
          lines: [{ shopProductSkuId: skuId, qty: vals.qty }],
          note,
        });
      } else if (mode === "lot") {
        const lots = vals.lots
          .map((l) => {
            const m = l.manufacturedDate.trim();
            const e = l.expiryDate.trim();
            return {
              lotNo: l.lotNo.trim(),
              qty: l.qty,
              ...(m ? { manufacturedDate: m } : {}),
              ...(e ? { expiryDate: e } : {}),
            };
          })
          .filter((l) => l.lotNo.length > 0);
        if (lots.length === 0) {
          toast.error("ໃສ່ຢ່າງນ້ອຍ 1 lot ທີ່ມີເລກ lot");
          return;
        }
        await receive.mutateAsync({
          branchId,
          defaultLocationId: receiveLocationId || undefined,
          lines: [{ shopProductSkuId: skuId, lots }],
          note,
        });
      } else if (mode === "lot_serial") {
        const serialReceipts: {
          serialNo: string;
          lotNo: string;
          unitScanCode?: string;
        }[] = [];
        for (const g of vals.serialLotGroups) {
          const lotNo = g.lotNo.trim();
          for (const it of g.items) {
            const serialNo = it.serialNo.trim();
            if (!serialNo) continue;
            if (!lotNo) {
              toast.error("ໃສ່ Lot no. ກ່ອນ ສຳລັບທຸກກຸ່ມທີ່ມີ serial");
              return;
            }
            let unitScan = it.unitScanCode?.trim() ?? "";
            if (!unitScan) {
              unitScan = buildDefaultUnitScanCode(lotNo, serialNo);
            }
            serialReceipts.push({
              serialNo,
              lotNo,
              ...(unitScan ? { unitScanCode: unitScan } : {}),
            });
          }
        }
        if (serialReceipts.length === 0) {
          toast.error(
            "ໃສ່ Lot no. ແລະ ຢ່າງນ້ອຍ 1 serial ຕໍ່ກຸ່ມ (ລະຫັດສະແກນຫນ່ວຍສ້າງອັດຕະໂນມັດຈາກ Lot + Serial)",
          );
          return;
        }
        await receive.mutateAsync({
          branchId,
          defaultLocationId: receiveLocationId || undefined,
          lines: [{ shopProductSkuId: skuId, serialReceipts }],
          note,
        });
      }
      nav({
        to: INVENTORY_ROUTE,
        params: { shopId },
      });
    } catch {
      // toast ຈາກ mutation
    }
  };

  const saveLotScanCode = async (lotId: string) => {
    const draft = lotScanDraftById[lotId];
    await patchLotScan.mutateAsync({
      lotId,
      body: { lotScanCode: draft == null ? null : draft },
    });
  };

  const regenerateLotScanCode = async (lotId: string) => {
    await patchLotScan.mutateAsync({
      lotId,
      body: { regenerate: true },
    });
  };

  if (!shopId) {
    return null;
  }

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.inventory.receive}
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventoryReceive(shopId)} />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div className="">
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ຮັບເຂົ້າສາງ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ເລືອກສາຂາ ແລະ SKU — ບັນທຶກຕາມໂໝດຕິດຕາມ (ລວມ / lot / lot+serial)
            </p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="mb-3 -ml-2 gap-1"
            onClick={() => {
              nav({
                to: INVENTORY_ROUTE,
                params: { shopId },
              });
            }}
          >
            <ArrowLeftIcon className="size-4" aria-hidden />
            ກັບສາງ
          </Button>
        </div>

        <div className="mx-auto max-w-5xl space-y-6 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
          <ReceiveWizardProgress step={wizardStep} />

          {wizardStep === 1 ? (
            <>
              <div className="space-y-2">
                <Label>ສາຂາ</Label>
                <InfiniteCombobox<ShopBranchOption>
                  queryKey={[
                    "shops",
                    shopId,
                    "inventory-receive-page",
                    "branch",
                  ]}
                  queryFn={async ({ search: q, pageParam }) => {
                    return shopInventoryApi.lookupBranches(shopId, {
                      page: pageParam,
                      search: q.trim() || undefined,
                      limit: 20,
                    });
                  }}
                  preloadQueryFn={(id) =>
                    shopInventoryApi.getBranch(shopId, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  value={form.watch("branchId") ?? ""}
                  onValueChange={(val) => {
                    form.setValue("branchId", val, { shouldValidate: true });
                    goReceiveSearch({
                      branchId: val || undefined,
                      shopProductSkuId: search.shopProductSkuId,
                    });
                  }}
                  placeholder="ເລືອກສາຂາ..."
                  className="h-9 w-full"
                  clearable
                />
              </div>

              <RHF.Controller
                name="branchId"
                control={form.control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <div className="space-y-2">
                <Label>ສິນຄ້າ / SKU</Label>
                <InfiniteCombobox<ShopSellableCatalogRow>
                  queryKey={["shops", shopId, "inventory-receive-page", "sku"]}
                  queryFn={async ({ search: q, pageParam }) => {
                    const limit = 20;
                    const page = pageParam ?? 1;
                    const offset = (page - 1) * limit;
                    const res = await shopCatalogApi.listSellable(shopId, {
                      offset,
                      limit,
                      q: q.trim() || undefined,
                      barcode: undefined,
                    });
                    const next =
                      offset + res.data.length < res.meta.total
                        ? page + 1
                        : null;
                    return { items: res.data, nextPage: next };
                  }}
                  preloadQueryFn={(id) =>
                    shopCatalogApi.getSellableSku(shopId, id)
                  }
                  getLabel={(item) => `${item.productName} · ${item.shopSku}`}
                  getValue={(item) => item.id}
                  value={urlSkuId ?? ""}
                  onValueChange={(id) => {
                    goReceiveSearch({
                      branchId: search.branchId,
                      shopProductSkuId: id || undefined,
                    });
                  }}
                  placeholder="ຄົ້ນຫາ: ຊື່, barcode, OEM..."
                  className="h-9 w-full"
                  clearable
                />
              </div>

              {urlSkuId && skuQuery.isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : null}

              {urlSkuId && !skuQuery.isLoading && skuQuery.isError ? (
                <p className="text-destructive text-sm">ໂຫຼດ SKU ບໍ່ສຳເລັດ</p>
              ) : null}

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    nav({
                      to: INVENTORY_ROUTE,
                      params: { shopId },
                    });
                  }}
                >
                  ຍົກເລີກ
                </Button>
                <Button
                  type="button"
                  disabled={!canAdvanceFromStep1}
                  onClick={() => setWizardStep(2)}
                >
                  ຕໍ່ໄປ
                </Button>
              </div>
            </>
          ) : null}

          {wizardStep >= 2 && urlSkuId && skuQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : null}

          {wizardStep >= 2 && activeSku ? (
            <InventorySkuStockBanner
              productName={activeSku.productName}
              shopSku={activeSku.shopSku}
              metaLines={[`ໂໝດ ${trackingLabel(mode)}`]}
              stockArea={
                !branchIdForStock ? (
                  <p className="text-muted-foreground text-sm sm:text-right">
                    ເລືອກສາຂາເພື່ອເຫັນຍອດຄົງເຫຼືອ
                  </p>
                ) : branchStock.isLoading ? (
                  <InventoryStockLoading />
                ) : (
                  <>
                    <InventoryStockHero
                      value={
                        stockLine?.quantityOnHand != null
                          ? String(stockLine.quantityOnHand)
                          : "—"
                      }
                    />
                    {stockLine != null &&
                    stockLine.quantityReserved != null &&
                    stockLine.quantityReserved > 0 ? (
                      <p className="mt-1 text-muted-foreground text-xs sm:text-right">
                        ຈອງ:{" "}
                        <span className="tabular-nums">
                          {stockLine.quantityReserved}
                        </span>
                      </p>
                    ) : null}
                    {stockLine != null &&
                    stockLine.quantityOnHand == null &&
                    !stockLine.inventoryTracked ? (
                      <p className="mt-1 text-muted-foreground text-xs leading-snug sm:text-right">
                        ຍັງບໍ່ມີແຖວສະຕ໋ອກທີ່ສາຂານີ້ (ຄັ້ງທຳອິດຈະເລີ່ມທີ່ 0)
                      </p>
                    ) : null}
                  </>
                )
              }
              footnote={
                activeSku.quantityOnHand != null ? (
                  <>
                    ລວມທົ່ວຮ້ານ (ທຸກສາຂາ):{" "}
                    <span className="font-medium text-foreground tabular-nums">
                      {activeSku.quantityOnHand}
                    </span>{" "}
                    ຊິ້ນ
                  </>
                ) : (
                  "ລວມທົ່ວຮ້ານ — ຍັງບໍ່ຕິດຕາມສະຕ໋ອກລວມ"
                )
              }
            />
          ) : null}

          {wizardStep >= 2 && activeSku ? (
            <FormRoot<ReceiveFormValues>
              methods={form}
              onSubmit={(v) => void onSubmit(v)}
              className="space-y-4"
            >
              {wizardStep === 2 && mode === "none" ? (
                <FormNumber
                  name="qty"
                  label="ຈຳນວນຮັບເຂົ້າ"
                  requiredMark
                  placeholder="ຕົວເລກເຕັມບວກ"
                />
              ) : null}

              {wizardStep === 2 && mode === "lot" ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>ລາຍການ lot</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 text-xs"
                      onClick={() =>
                        append({
                          lotNo: "",
                          qty: 1,
                          manufacturedDate: "",
                          expiryDate: "",
                        })
                      }
                    >
                      <PlusIcon className="size-3.5" />
                      ເພີ່ມ lot
                    </Button>
                  </div>
                  <div className="max-h-[28rem] space-y-3 overflow-y-auto rounded-md border border-border p-3">
                    {fields.map((f, i) => (
                      <div
                        key={f.id}
                        className="grid gap-3 border-border border-b pb-3 last:border-0 sm:grid-cols-12"
                      >
                        <div className="sm:col-span-3">
                          <Label className="text-xs">Lot no.</Label>
                          <Input
                            className="mt-1 h-9 font-mono text-sm"
                            {...form.register(`lots.${i}.lotNo`)}
                            placeholder="LOT-001"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">ຈຳນວນ</Label>
                          <Input
                            type="number"
                            min={1}
                            className="mt-1 h-9 text-sm"
                            {...form.register(`lots.${i}.qty`, {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <Label className="text-xs">ວັນທີຜະລິດ</Label>
                          <RHF.Controller
                            control={form.control}
                            name={`lots.${i}.manufacturedDate`}
                            render={({ field }) => (
                              <DatePickerField
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                placeholder="ເລືອກວັນທີ"
                                className="mt-1"
                                yearsBehind={50}
                                yearsAhead={50}
                              />
                            )}
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <Label className="text-xs">ໝົດອາຍຸ</Label>
                          <RHF.Controller
                            control={form.control}
                            name={`lots.${i}.expiryDate`}
                            render={({ field }) => (
                              <DatePickerField
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                placeholder="ເລືອກວັນທີ"
                                className="mt-1"
                                yearsBehind={50}
                                yearsAhead={50}
                              />
                            )}
                          />
                        </div>
                        <div className="flex items-end sm:col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 text-destructive"
                            disabled={fields.length <= 1}
                            onClick={() => remove(i)}
                            aria-label="ລົບ lot"
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 rounded-md border border-border/70 bg-muted/20 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">
                        Lot scan labels (Code 128)
                      </Label>
                      {lotLabelsQuery.isFetching ? <Loader /> : null}
                    </div>
                    {!branchIdForStock || !urlSkuId ? (
                      <p className="text-muted-foreground text-xs">
                        ເລືອກສາຂາ ແລະ SKU ເພື່ອຈັດການລະຫັດສະແກນ lot
                      </p>
                    ) : lotLabelsQuery.isLoading ? (
                      <div className="flex justify-center py-3">
                        <Loader />
                      </div>
                    ) : lotLabelsQuery.isError ? (
                      <p className="text-destructive text-xs">
                        {lotLabelsQuery.error instanceof Error
                          ? lotLabelsQuery.error.message
                          : "ໂຫຼດ lot labels ບໍ່ສຳເລັດ"}
                      </p>
                    ) : !lotLabelsQuery.data?.items?.length ? (
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ພົບ lot ສຳລັບ SKU ນີ້ໃນສາຂານີ້
                      </p>
                    ) : (
                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {lotLabelsQuery.data.items.map((lot) => {
                          const draft = lotScanDraftById[lot.id];
                          const live = draft ?? lot.lotScanCode ?? "";
                          return (
                            <div
                              key={lot.id}
                              className="grid gap-2 rounded-md border border-border/70 bg-card p-2 sm:grid-cols-12 sm:items-end"
                            >
                              <div className="sm:col-span-2">
                                <Label className="text-xs">Lot</Label>
                                <p className="mt-1 font-mono text-xs">
                                  {lot.lotNo}
                                </p>
                              </div>
                              <div className="sm:col-span-4">
                                <Label className="text-xs">Scan code</Label>
                                <Input
                                  className="mt-1 h-9 font-mono text-xs"
                                  value={live}
                                  onChange={(e) =>
                                    setLotScanDraftById((p) => ({
                                      ...p,
                                      [lot.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="LXXXXXXXXXXXXXX"
                                />
                              </div>
                              <div className="flex flex-wrap gap-1 sm:col-span-6 sm:justify-end">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs"
                                  onClick={() => void saveLotScanCode(lot.id)}
                                  disabled={patchLotScan.isPending}
                                >
                                  ບັນທຶກ
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs"
                                  onClick={() =>
                                    void regenerateLotScanCode(lot.id)
                                  }
                                  disabled={patchLotScan.isPending}
                                >
                                  ສ້າງໃໝ່
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 text-xs"
                                  onClick={() =>
                                    printLotLabelCode128({
                                      lotScanCode: live,
                                      lotNo: lot.lotNo,
                                      productName: activeSku.productName,
                                      shopSku: activeSku.shopSku,
                                      expiryDate: lot.expiryDate,
                                    })
                                  }
                                  disabled={!live}
                                >
                                  ພິມປ້າຍ
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {wizardStep === 2 && mode === "lot_serial" ? (
                <div className="space-y-4">
                  <p className="text-muted-foreground text-xs leading-snug">
                    ແຕ່ລະກຸ່ມ = 1 lot — ໃສ່ Lot no. ກ່ອນ ຈາກນັ້ນເພີ່ມ serial ຕໍ່ຊິ້ນໃນກຸ່ມນັ້ນ.
                    ລະຫັດສະແກນຫນ່ວຍສ້າງອັດຕະໂນມັດເປັນ{" "}
                    <span className="font-mono text-foreground">
                      Lot|Serial
                    </span>{" "}
                    (ແກ້ໄດ້ຖ້າຕ້ອງການ)
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">ກຸ່ມຕາມ Lot</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 text-xs"
                      onClick={() =>
                        appendSerialGroup({
                          lotNo: "",
                          items: [{ serialNo: "", unitScanCode: "" }],
                        })
                      }
                    >
                      <PlusIcon className="size-3.5" />
                      ເພີ່ມກຸ່ມ lot
                    </Button>
                  </div>
                  <div className="max-h-[32rem] space-y-4 overflow-y-auto pr-1">
                    {serialGroupFields.map((g, gi) => (
                      <SerialLotGroupBlock
                        key={g.id}
                        groupIndex={gi}
                        form={form}
                        canRemoveGroup={serialGroupFields.length > 1}
                        onRemoveGroup={() => removeSerialGroup(gi)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {wizardStep === 3 ? (
                <div className="space-y-3 rounded-lg border border-border/70 bg-muted/20 p-4">
                  <p className="font-medium text-sm">ສະຫຼຸບກ່ອນບັນທຶກ</p>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-muted-foreground text-xs">ສາຂາ</dt>
                      <dd className="font-medium">
                        {branchDetailQuery.data?.name ??
                          branchIdForStock ??
                          "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">ສິນຄ້າ</dt>
                      <dd className="font-medium">{activeSku.productName}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">SKU</dt>
                      <dd className="font-mono text-muted-foreground">
                        {activeSku.shopSku}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">
                        ຈຳນວນຮັບເຂົ້າ
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {receiveSummaryLine()}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-muted-foreground text-xs">ໂໝດ</dt>
                      <dd>{trackingLabel(mode)}</dd>
                    </div>
                  </dl>
                </div>
              ) : null}

              {wizardStep === 3 && warehouseLocationsOn && branchIdForStock ? (
                <div className="space-y-2">
                  <p className="font-medium text-sm">ຊ່ອງຮັບເຂົ້າ</p>
                  <ShopWarehouseLocationPickerRemote
                    shopId={shopId!}
                    branchId={branchIdForStock}
                    value={receiveLocationId}
                    onValueChange={setReceiveLocationId}
                    placeholder="default receive (ວ່າງ = MAIN-DEFAULT)"
                    className="w-full max-w-md"
                  />
                  <p className="text-muted-foreground text-xs">
                    ວ່າງໄວ້ = ໃຊ້ຊ່ອງ default ຂອງສາຂາ
                  </p>
                </div>
              ) : null}

              {wizardStep === 3 ? (
                <FormTextarea
                  name="note"
                  label="ໝາຍເຫດ (ທາງເລືອກ)"
                  placeholder="ອ້າງອີງໃບສົ່ງ..."
                  rows={2}
                />
              ) : null}

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    if (wizardStep === 2) {
                      setWizardStep(1);
                      return;
                    }
                    setWizardStep(2);
                  }}
                >
                  ກັບຄືນ
                </Button>
                {wizardStep === 2 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (validateStep2()) setWizardStep(3);
                    }}
                  >
                    ຕໍ່ໄປ
                  </Button>
                ) : (
                  <Button type="submit" disabled={receive.isPending}>
                    {receive.isPending ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader />
                        ກຳລັງບັນທຶກ
                      </span>
                    ) : (
                      "ບັນທຶກຮັບເຂົ້າ"
                    )}
                  </Button>
                )}
              </div>
            </FormRoot>
          ) : null}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
